import { isApiBusy } from "@apis/index";
import { isAuthenticated, logout } from "@utils/auth";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const useInactivityTimer = (inactivityTimeMs = 3600000, enabled = true) => {
  const timeoutIdRef = useRef(null);
  const navigate = useNavigate();
  const enabledRef = useRef(enabled);
  const inactivityTimeMsRef = useRef(inactivityTimeMs);
  const lastMouseMoveTimeRef = useRef(0);
  const MOUSE_MOVE_THROTTLE_MS = 1000; // Throttle mousemove to once per second
  const API_BUSY_RECHECK_MS = 300000; // Recheck every 5 minutes if API is still busy

  // Update refs when props change
  useEffect(() => {
    enabledRef.current = enabled;
    inactivityTimeMsRef.current = inactivityTimeMs;
  }, [enabled, inactivityTimeMs]);

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    // Only set timer if user is authenticated and hook is enabled
    if (!enabledRef.current || !isAuthenticated()) {
      return;
    }

    // Set new timer
    const attemptLogout = async () => {
      // Do not proceed if hook disabled or user not authenticated anymore
      if (!enabledRef.current || !isAuthenticated()) {
        return;
      }
      // If API is currently busy, defer logout and recheck later
      if (isApiBusy()) {
        timeoutIdRef.current = setTimeout(attemptLogout, API_BUSY_RECHECK_MS);
        return;
      }
      // Proceed with logout
      try {
        await logout(true, { clearIndexedDb: true });
        navigate("/login", { replace: true });
      } catch (error) {
        console.error("Error during inactivity logout:", error);
        // Even if logout fails, still navigate to login for security
        navigate("/login", { replace: true });
      }
    };

    timeoutIdRef.current = setTimeout(attemptLogout, inactivityTimeMsRef.current);
  }, [navigate]);

  // Activity handler with throttling for mousemove events
  const handleActivity = useCallback(
    (event) => {
      // Throttle mousemove events to reduce performance impact
      // For other events, reset timer immediately
      if (event?.type === "mousemove") {
        const now = Date.now();
        if (now - lastMouseMoveTimeRef.current < MOUSE_MOVE_THROTTLE_MS) {
          return; // Skip this mousemove event
        }
        lastMouseMoveTimeRef.current = now;
      }

      resetTimer();
    },
    [resetTimer],
  );

  useEffect(() => {
    // Only set up listeners if enabled
    if (!enabled) {
      // Clear timer if disabled
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      return;
    }

    // Check authentication on mount
    if (!isAuthenticated()) {
      return;
    }

    // List of events that indicate user activity
    // Use document events only (more reliable and avoids duplicate triggers)
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "wheel", // For mouse wheel
      "contextmenu", // Right click
    ];

    // Add window-specific events
    const windowEvents = ["focus", "blur"];

    // Add event listeners with passive option for better performance
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Add window-specific events
    windowEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize timer
    resetTimer();

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });

      windowEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      // Clear timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [enabled, handleActivity, resetTimer]);

  // Return resetTimer function for manual control if needed
  return { resetTimer };
};

export default useInactivityTimer;
